import { cn } from "@/lib/utils"

// A minimal ARIA tab bar. The design system has no Tabs primitive and
// components/ui is vendored, so this lives here rather than growing the
// shared surface for one product's page.
//
// Counts are part of the label on purpose: "Work Orders 14" tells an operator
// whether opening the tab is worth it before they click.

export interface FleetTab {
  key: string
  label: string
  count?: number | null
}

export function TabBar({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: FleetTab[]
  active: string
  onChange: (key: string) => void
  label: string
}) {
  return (
    <div
      role="tablist"
      aria-label={label}
      className="border-border flex flex-wrap items-center gap-1 border-b"
    >
      {tabs.map((tab) => {
        const selected = tab.key === active
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            id={`tab-${tab.key}`}
            aria-selected={selected}
            aria-controls={`panel-${tab.key}`}
            onClick={() => onChange(tab.key)}
            className={cn(
              "-mb-px cursor-pointer border-b-2 px-3 py-2 text-sm transition-colors",
              selected
                ? "border-foreground text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {tab.label}
            {tab.count != null && (
              <span className="text-muted-foreground ml-1.5 tabular-nums">
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

export function TabPanel({
  tabKey,
  active,
  children,
}: {
  tabKey: string
  active: string
  children: React.ReactNode
}) {
  if (tabKey !== active) return null

  return (
    <div
      role="tabpanel"
      id={`panel-${tabKey}`}
      aria-labelledby={`tab-${tabKey}`}
      className="pt-4"
    >
      {children}
    </div>
  )
}
